import { SUPABASE_PUBLISHABLE_KEY } from '../lib/supabase';
import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { fetchWithTimeout, bookSearchHeaders } from '../lib/bookSearch';
import { findOrCreateBook, normalizeIsbn } from '../lib/findOrCreateBook';
// A bulk import is not a typeahead. It can afford to wait longer than a search
// box for a slow-but-working source, but it must still give up rather than
// leave the import sitting on a row forever.
const IMPORT_TIMEOUT_MS = 15000;
import { getCurrentUser } from '../lib/auth';
import { useToast } from './Toast';
import Papa from 'papaparse';
import { resolveAllTagsToBookTags, escapeLike, tagNameError, writeReaderGenrePicks } from '../lib/tropeUtils';
import { useGenreTaxonomy } from '../hooks/useGenreTaxonomy';
import GenreSubgenrePicker from './genre/GenreSubgenrePicker';

import { onCoverError } from '../lib/coverFallback';
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// TAG_COLORS.genres is deleted here (PR3, docs/genre-subgenre-build-
// spec.md, Control A surface #5) -- superseded by the real genres/
// subgenres tables via GenreSubgenrePicker below. tropes/moods/warnings
// are untouched.
const TAG_COLORS = {
  tropes:   { bg: '#fef3c7', color: 'var(--feedback-warning-ink)', border: 'var(--feedback-warning-border)' },
  moods:    { bg: '#ede9fe', color: '#5b21b6', border: '#c4b5fd' },
  warnings: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
};

// Job 2 (docs/genre-subgenre-build-spec.md :1719-1755, reviewer's second
// pass) -- the old separate floors are replaced by one combined trope+mood
// minimum. Content warnings never count toward it, here or anywhere else.
const MIN_TAGS = 3;

// Same look the old flat genre TagRow used, handed to GenreSubgenrePicker
// as its injected Chip.
function CsvGenreChip({ on, onClick, children }) {
  return (
    <button onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 'var(--font-size-xs)', padding: '3px 10px', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: on ? 700 : 500,
      border: on ? '1px solid #93c5fd' : '1px dashed #93c5fd', background: on ? '#dbeafe' : 'transparent', color: '#1e40af' }}>
      {children}
    </button>
  );
}

const SPICE_LABELS = ['No Spice', 'Mild', 'Moderate', 'Spicy', 'Scorching'];
const SPICE_DESCS  = ['Closed door', 'Fade to black', 'Some heat', 'Explicit', 'Very explicit'];

function TagRow({ icon, label, tags, colorKey, onRemove, onAdd, required }) {
  const [adding, setAdding] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [tagWarning, setTagWarning] = useState('');
  const c = TAG_COLORS[colorKey] || TAG_COLORS.tropes;
  // One component backs all four rows (genres, tropes, moods, warnings), so the
  // guard goes here rather than at the four call sites.
  const warningId = `csv-tag-warning-${colorKey}`;

  function submit() {
    const val = newTag.trim();
    if (!val) return;
    // Refuse rather than truncate, and leave their text alone so they can edit
    // it down. See tagNameError in tropeUtils for why this is not a maxLength.
    const problem = tagNameError(val);
    if (problem) { setTagWarning(problem); return; }
    onAdd(val);
    setNewTag('');
    setTagWarning('');
    setAdding(false);
  }
  return (
    <div style={{ marginBottom: 'var(--space-10)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-6)' }}>
        <span style={{ fontSize: 'var(--font-size-sm)' }}>{icon}</span>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: 0.5 }}>{label}</span>
        {required && <span style={{ fontSize: 'var(--font-size-micro)', color: 'var(--feedback-error)' }}>*</span>}
        <button onClick={() => setAdding(!adding)} style={{ marginLeft: 'auto', fontSize: 'var(--font-size-tiny)', color: 'var(--brand-rust)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
          {adding ? 'Done' : '+ Add'}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
        {(tags || []).map((t, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 'var(--font-size-xs)', padding: '3px 10px', borderRadius: 'var(--radius-md)', background: c.bg, color: c.color, fontWeight: 500, border: `1px solid ${c.border}` }}>
            {t}
            <span onClick={() => onRemove(i)} style={{ cursor: 'pointer', opacity: 0.5, fontSize: 'var(--font-size-tiny)', marginLeft: 'var(--space-xxs)' }}>✕</span>
          </span>
        ))}
        {(!tags || tags.length === 0) && !adding && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontStyle: 'italic' }}>None added yet</span>}
        {adding && (
          <input value={newTag}
            onChange={e => { setNewTag(e.target.value); if (tagWarning) setTagWarning(''); }}
            onKeyDown={e => {
              if (e.key === 'Enter') submit();
              if (e.key === 'Escape') { setAdding(false); setNewTag(''); setTagWarning(''); }
            }}
            placeholder="Type and press Enter..." autoFocus
            aria-invalid={tagWarning ? 'true' : undefined}
            aria-describedby={tagWarning ? warningId : undefined}
            style={{ fontSize: 'var(--font-size-xs)', padding: '3px 10px', borderRadius: 'var(--radius-md)', border: tagWarning ? '1px dashed var(--feedback-warning-border)' : '1px dashed var(--feedback-warning)', outline: 'none', minWidth: 140 }} />
        )}
      </div>
      {tagWarning && (
        <p id={warningId} role="status" style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-warning-ink)', margin: '6px 0 0', lineHeight: 1.4 }}>
          {tagWarning}
        </p>
      )}
    </div>
  );
}

export default function CSVUploadModal({ onClose, onComplete }) {
  const { addToast } = useToast();

  // steps: upload | processing | stats | select | enriching | review | done
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);

  // Processing all books
  const [processProgress, setProcessProgress] = useState({ current: 0, total: 0, label: '' });

  // Stats from all books
  const [stats, setStats] = useState(null);

  // 5-star books
  const [fiveStarBooks, setFiveStarBooks] = useState([]);
  const [selectedIndexes, setSelectedIndexes] = useState(new Set());

  // Enriching
  const [enrichProgress, setEnrichProgress] = useState({ current: 0, total: 0, label: '' });
  const [enrichedBooks, setEnrichedBooks] = useState([]);

  // Review
  const [cardIndex, setCardIndex] = useState(0);
  const [decisions, setDecisions] = useState({});
  const [swipeDir, setSwipeDir] = useState(null);
  // Real genres/subgenres tables (PR3, Control A surface #5) tracked as
  // their own per-card id maps, parallel to editTags -- GenreSubgenrePicker
  // needs ids, and subgenre is a concept the old flat genres TagRow never
  // had. Keyed by card index, same shape as editTags itself.
  const { genres: csvGenres, subgenres: csvSubgenres, subgenresById: csvSubgenresById,
    canonSubgenresForGenre: csvCanonSubgenresForGenre, matchIndex: csvMatchIndex,
    upsertPendingSubgenre: csvUpsertPendingSubgenre,
    loading: csvGenreLoading, error: csvGenreError } = useGenreTaxonomy();
  const [editGenreIds, setEditGenreIds] = useState({});
  const [editSubgenreIds, setEditSubgenreIds] = useState({});
  const [editSubgenreNamesById, setEditSubgenreNamesById] = useState({});
  // Whether this book has no genre yet from ANY source (author, reader, or
  // api) -- the same book_tags/tag_type='genre' test ProfileBlocks.jsx's
  // journal uses, reused here rather than a second query (docs/genre-
  // subgenre-build-spec.md, "never ask twice"). Defaults true until the
  // platform lookup below resolves it per card.
  const [genreNeeded, setGenreNeeded] = useState({});
  const [editTags, setEditTags] = useState({});

  const [saving, setSaving] = useState(false);

  // ── Helpers ───────────────────────────────────────────────────────────────
  function readCol(row, ...keys) {
    for (const key of keys) {
      const val = row[key]?.toString().trim();
      if (val && val !== 'undefined') return val;
    }
    return null;
  }

  function mapShelf(raw) {
    const s = raw?.toLowerCase().trim() || '';
    if (['read', 'finished', 'completed', 'done'].includes(s)) return 'read';
    if (['currently-reading', 'currently reading', 'reading', 'in progress'].includes(s)) return 'reading';
    if (['did-not-finish', 'dnf', 'abandoned'].includes(s)) return 'dnf';
    if (['to-read', 'to read', 'want to read', 'tbr', 'wish list', 'wishlist'].includes(s)) return 'tbr';
    return 'tbr';
  }

  function handleFileSelect(e) {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.endsWith('.csv')) { addToast('Please upload a CSV file', 'error'); return; }
    setFile(f);
  }

  // ── STEP 1: Process ALL books for stats + library ─────────────────────────
  async function handleUpload() {
    if (!file) return;
    setStep('processing');

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (result) => {
        const rows = result.data;
        const user = await getCurrentUser();
        if (!user) { addToast('Please log in', 'error'); setStep('upload'); return; }

        setProcessProgress({ current: 0, total: rows.length, label: 'Reading your library...' });

        let totalBooks = rows.length;
        let readCount = 0;
        let fiveStarCount = 0;
        const genreCounts = {};
        const ratingCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        const fiveStars = [];

        const uniqueAuthorsSet = new Set();
        let totalPages = 0;
        let totalRatingSum = 0;
        let totalRatedCount = 0;

        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];

          if (i % 20 === 0) {
            setProcessProgress({ current: i + 1, total: rows.length, label: `Reading your library... ${i + 1} of ${rows.length}` });
            await sleep(10);
          }

          const title = readCol(row, 'Title', 'title', 'Book Title') || null;
          const author = readCol(row, 'Author', 'author', 'Author Name', 'Authors') || null;
          const isbn = readCol(row, 'ISBN', 'isbn', 'ISBN10')?.replace(/[^0-9X]/gi, '') || null;
          const isbn13 = readCol(row, 'ISBN13', 'isbn13', 'ISBN-13')?.replace(/[^0-9]/g, '') || null;
          const goodreadsId = readCol(row, 'Book Id', 'book id', 'Goodreads Id') || null;
          const ratingRaw = parseFloat(readCol(row, 'My Rating', 'Rating', 'Star Rating') || '0');
          const shelfRaw = readCol(row, 'Exclusive Shelf', 'Read Status', 'Status', 'Bookshelves') || 'to-read';
          const shelf = mapShelf(shelfRaw);
          const numPagesRaw = readCol(row, 'Number of Pages', 'Number Of Pages', 'pages', 'page_count') || null;
          const pageCount = numPagesRaw ? parseInt(numPagesRaw) || 0 : 0;
          const genresRaw = readCol(row, 'Genres', 'genres', 'Tags') || '';

          if (!title || !author) continue;

          if (ratingRaw >= 1 && ratingRaw <= 5) {
            ratingCounts[Math.round(ratingRaw)]++;
            totalRatingSum += ratingRaw;
            totalRatedCount++;
          }

          if (shelf === 'read') {
            readCount++;
            uniqueAuthorsSet.add(author.toLowerCase().trim());
            totalPages += pageCount;
            genresRaw.split(',').map(g => g.trim()).filter(Boolean).forEach(g => {
              genreCounts[g] = (genreCounts[g] || 0) + 1;
            });
          }

          if (ratingRaw === 5) {
            fiveStarCount++;
            fiveStars.push({ title, author, isbn, isbn13, goodreadsId });
          }
        }

        setProcessProgress({ current: rows.length, total: rows.length, label: 'Building your stats...' });

        // null, not 0. An import where nothing was rated has no average, and a
        // stored 0 is indistinguishable from "rated everything zero" once it is
        // in the table — which is how the admin CSV page came to print ★ 0.0
        // and to drag the community average down with it. The column is
        // numeric(3,2) DEFAULT 0 and nullable (live_schema_snapshot.sql:3014),
        // so null stores fine. Rows written before this change still hold 0,
        // which is why readers of this column treat 0 as unknown.
        const avgRating = totalRatedCount > 0 ? Math.round((totalRatingSum / totalRatedCount) * 100) / 100 : null;
        const topGenres = Object.entries(genreCounts).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([g]) => g);

        try {
          await supabase.from('user_csv_stats').upsert({
            user_id: user.id,
            books_read: readCount,
            total_pages: totalPages,
            avg_rating: avgRating,
            unique_authors: uniqueAuthorsSet.size,
            five_star_count: fiveStarCount,
            rating_breakdown: ratingCounts,
            top_genres: topGenres,
            updated_at: new Date().toISOString(),
          }, { onConflict: 'user_id' });
        } catch (err) {
          console.warn('Stats save error:', err.message);
        }

        setStats({ totalBooks, readCount, fiveStarCount, ratingCounts, topGenres });
        setFiveStarBooks(fiveStars);
        setSelectedIndexes(new Set(fiveStars.map((_, i) => i)));
        setStep('stats');
      },
      error: () => {
        addToast('Failed to parse CSV', 'error');
        setStep('upload');
      },
    });
  }

  // ── STEP 3: Enrich selected 5-star books (cover/blurb only — no auto-tagging) ──
  async function handleStartEnriching() {
    const selected = fiveStarBooks.filter((_, i) => selectedIndexes.has(i));
    if (selected.length === 0) return;

    setStep('enriching');
    setEnrichProgress({ current: 0, total: selected.length, label: 'Starting...' });

    const { data: { session } } = await supabase.auth.getSession();
    const headers = bookSearchHeaders(session, SUPABASE_PUBLISHABLE_KEY);
    const baseUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/book-search`;
    const goodreadsUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/goodreads-book-details`;

    const enriched = [];
    for (let i = 0; i < selected.length; i++) {
      const book = selected[i];
      setEnrichProgress({ current: i + 1, total: selected.length, label: `Finding: ${book.title}...` });

      let data = { ...book, cover_image_url: null, blurb: null, genres: [], work_key: null, ratings_count: null, edition_count: null, popularity_checked_at: null };

      try {
        // 1. Goodreads API first if goodreadsId available — cover/blurb/genres only
        if (book.goodreadsId) {
          try {
            // Goes through an edge function rather than straight to the
            // provider — the RapidAPI key lives in a function secret now, not
            // in the browser bundle.
            const grRes = await fetchWithTimeout(goodreadsUrl, {
              method: 'POST',
              headers,
              body: JSON.stringify({ goodreadsId: book.goodreadsId }),
            }, IMPORT_TIMEOUT_MS);
            if (grRes.ok) {
              const { book: gr } = await grRes.json();
              if (gr?.title) {
                data.cover_image_url = gr.imageURL?.replace(/^http:/, 'https:') || null;
                data.blurb = gr.description || null;
                data.page_count = gr.pages ? parseInt(gr.pages) : null;
                data.avg_rating = gr.rating ? parseFloat(gr.rating) : null;
                const genericShelves = new Set(['to-read','read','currently-reading','favorites','owned','kindle','ebook','audiobook','library','fiction','non-fiction','default']);
                const grGenres = (gr.genres || []).filter(g => !genericShelves.has(g.toLowerCase()));
                if (grGenres.length > 0) data.genres = grGenres.slice(0, 5);
              }
            }
            await sleep(500);
          } catch (err) { console.warn('Goodreads error:', err.message); }
        }

        // 2. Open Library fallback — cover/blurb only
        //
        // Popularity signal (reader #110 step two): every d.book response here is a real
        // book-search answer, so it is when the ONLY time this block runs
        // that ratings_count/edition_count can be captured for a CSV import.
        // If Goodreads above already supplied both blurb and cover, this
        // whole block — and the only chance to check popularity for this
        // book in this flow — is skipped entirely. That is a pre-existing
        // gap in when popularity gets checked, not something this ticket
        // changes; see the PR body.
        if (!data.blurb || !data.cover_image_url) {
          const isbn = book.isbn13 || book.isbn;
          if (isbn) {
            const res = await fetchWithTimeout(baseUrl, { method: 'POST', headers, body: JSON.stringify({ isbn }) }, IMPORT_TIMEOUT_MS);
            if (res.ok) {
              const d = await res.json();
              if (d.book) {
                if (!data.cover_image_url) data.cover_image_url = d.book.cover_image_url;
                if (!data.blurb) data.blurb = d.book.blurb;
                if (!data.genres?.length) data.genres = d.book.genres || [];
                data.work_key = d.book.work_key;
                if (data.ratings_count == null) data.ratings_count = d.book.ratings_count ?? null;
                if (data.edition_count == null) data.edition_count = d.book.edition_count ?? null;
                data.popularity_checked_at = new Date().toISOString();
              }
            }
          }
          if (!data.blurb) {
            const res = await fetchWithTimeout(baseUrl, { method: 'POST', headers, body: JSON.stringify({ title: book.title, author: book.author }) }, IMPORT_TIMEOUT_MS);
            if (res.ok) {
              const d = await res.json();
              if (d.book) {
                if (!data.cover_image_url) data.cover_image_url = d.book.cover_image_url;
                if (!data.blurb) data.blurb = d.book.blurb;
                if (!data.genres?.length) data.genres = d.book.genres || [];
                data.work_key = d.book.work_key;
                if (data.ratings_count == null) data.ratings_count = d.book.ratings_count ?? null;
                if (data.edition_count == null) data.edition_count = d.book.edition_count ?? null;
                data.popularity_checked_at = new Date().toISOString();
              }
            }
          }
        }

      // 3. Check if book already exists on platform with tags from a previous
// submission — pre-fill as a starting point so posters don't retype tags
// for books others have already tagged. Always editable.
let existingTags = { genres: data.genres || [], tropes: [], moods: [], warnings: [], spice: 'Moderate' };
let hasGenreTag = false;
try {
  const isbn13 = normalizeIsbn(book.isbn13 || book.isbn);
  let existingBook = null;

  if (isbn13) {
    const { data: byIsbn } = await supabase
      .from('books')
      .select('id, book_tags ( tag_type, tropes ( canonical_name ) ), book_genres ( genre_id )')
      .eq('isbn13', isbn13)
      .order('id', { ascending: true })
      .limit(1)
      .maybeSingle();
    existingBook = byIsbn;
  }
  if (!existingBook) {
    const { data: byTitleAuthor } = await supabase
      .from('books')
      .select('id, book_tags ( tag_type, tropes ( canonical_name ) ), book_genres ( genre_id )')
      .ilike('title', escapeLike(book.title))
      .ilike('author_name', escapeLike(book.author))
      .order('id', { ascending: true })
      .limit(1)
      .maybeSingle();
    existingBook = byTitleAuthor;
  }

  // Genre lives in book_genres now (PR5, docs/genre-subgenre-build-spec.md) --
  // a new book's genre is never written to book_tags, so checking book_tags
  // alone here was the #163 loop again: a book genred only through
  // book_genres came back with hasGenreTag false and got asked for a genre
  // it already had, on every single import. Same failure, same fix, as
  // ProfileBlocks.jsx's searchBooksWithSource query (that fix's own comment
  // names this exact loop). Checked off existingBook directly, independent
  // of book_tags.length, so a book with zero book_tags rows but a
  // book_genres row still counts as having a genre.
  hasGenreTag = (existingBook?.book_genres || []).length > 0;

  if (existingBook?.book_tags?.length > 0) {
    const fromPlatform = { tropes: [], moods: [], genres: data.genres || [], spice: 'Moderate' };
    existingBook.book_tags.forEach(bt => {
      const name = bt.tropes?.canonical_name;
      if (!name) return;
      if (bt.tag_type === 'trope') fromPlatform.tropes.push(name);
      else if (bt.tag_type === 'tone') fromPlatform.moods.push(name);
      else if (bt.tag_type === 'spice') fromPlatform.spice = name;
      else if (bt.tag_type === 'genre') fromPlatform.genres.push(name);
    });
    existingTags = { ...fromPlatform, warnings: [] };
  }
} catch (err) {
  console.warn('Could not check existing platform tags:', err.message);
}

        enriched.push({ ...data, tags: existingTags, genreNeeded: !hasGenreTag });
      } catch (err) {
        console.warn('Lookup failed for', book.title, err.message);
        enriched.push({ ...data, tags: { genres: data.genres || [], tropes: [], moods: [], warnings: [], spice: 'Moderate' }, genreNeeded: true });
      }

      await sleep(300);
    }

    const initialEditTags = {};
    const initialGenreIds = {};
    const initialSubgenreIds = {};
    const initialGenreNeeded = {};
    enriched.forEach((b, i) => {
      initialEditTags[i] = { ...b.tags };
      initialGenreNeeded[i] = b.genreNeeded !== false;
      const gIds = [];
      const sIds = [];
      (b.tags.genres || []).forEach(name => {
        const g = csvGenres.find(pg => pg.name === name);
        if (g) { if (!gIds.includes(g.id)) gIds.push(g.id); return; }
        const sg = csvSubgenres.find(s => s.name === name);
        if (sg) {
          if (!sIds.includes(sg.id)) sIds.push(sg.id);
          if (!gIds.includes(sg.genre_id)) gIds.push(sg.genre_id);
        }
        // Neither a canon genre nor a canon subgenre name (a raw CSV/API
        // genre string, or an older ad-hoc book_tags entry from before
        // PR3) -- drop it rather than guess.
      });
      initialGenreIds[i] = gIds;
      initialSubgenreIds[i] = sIds;
    });
    setEditTags(initialEditTags);
    setEditGenreIds(initialGenreIds);
    setEditSubgenreIds(initialSubgenreIds);
    setGenreNeeded(initialGenreNeeded);
    setEnrichedBooks(enriched);
    setCardIndex(0);
    setDecisions({});
    setStep('review');
    setIsMinimized(false);
  }

  // ── Tag editing ───────────────────────────────────────────────────────────
  function removeTag(bi, type, ti) {
    setEditTags(p => ({ ...p, [bi]: { ...p[bi], [type]: p[bi][type].filter((_, i) => i !== ti) } }));
  }
  function addTag(bi, type, val) {
    setEditTags(p => ({ ...p, [bi]: { ...p[bi], [type]: [...(p[bi][type] || []), val] } }));
  }
  function setSpice(bi, val) {
    setEditTags(p => ({ ...p, [bi]: { ...p[bi], spice: val } }));
  }

  // ── Card decision ─────────────────────────────────────────────────────────
  async function handleDecision(decision) {
    setSwipeDir(decision === 'approved' ? 'right' : 'left');
    setDecisions(p => ({ ...p, [cardIndex]: decision }));

    await new Promise(res => setTimeout(res, 350));
    setSwipeDir(null);

    if (decision === 'approved') {
      setSaving(true);
      await saveBook(enrichedBooks[cardIndex], {
        ...editTags[cardIndex],
        genreIds:           editGenreIds[cardIndex] || [],
        subgenreIds:        editSubgenreIds[cardIndex] || [],
        subgenreNamesById:  editSubgenreNamesById[cardIndex] || {},
      });
      setSaving(false);
    }

    if (cardIndex < enrichedBooks.length - 1) setCardIndex(cardIndex + 1);
    else setStep('done');
  }

  async function saveBook(book, tags) {
    try {
      const user = await getCurrentUser();
      if (!user) return;

      // Real genre/subgenre picks (PR3, Control A surface #5) -- both ride
      // the 'genre' group below, same rule every other PR3 surface uses.
      const genreNames = (tags.genreIds || [])
        .map(id => csvGenres.find(g => g.id === id)?.name)
        .filter(Boolean);
      const subgenreNames = (tags.subgenreIds || [])
        .map(id => csvSubgenresById.get(id)?.name || tags.subgenreNamesById?.[id]?.name)
        .filter(Boolean);
      const allGenreNames = [...genreNames, ...subgenreNames];

      // Popularity signal (reader #110 step two): book.popularity_checked_at is only
      // set above when a real book-search response came back for this row
      // (see the Open Library fallback block in handleStartEnriching) — a
      // book fully served by Goodreads never reaches that code and stays
      // null here, which is correct: it was never checked.
      const popularityChecked = book.popularity_checked_at != null;
      const popularityFields = popularityChecked
        ? {
            ratings_count: book.ratings_count ?? null,
            edition_count: book.edition_count ?? null,
            popularity_checked_at: book.popularity_checked_at,
          }
        : {};

      const bookId = await findOrCreateBook(
        { title: book.title, author_name: book.author, isbn13: book.isbn13 || book.isbn },
        {
          submission_type: 'csv',
          extra: {
            cover_image_url: book.cover_image_url || null,
            blurb:           book.blurb || null,
            genres:          allGenreNames,
            avg_rating:      book.avg_rating ?? null,
            ...popularityFields,
          },
          fillIfNull: popularityChecked
            ? { ratings_count: book.ratings_count ?? null, edition_count: book.edition_count ?? null }
            : null,
        }
      );

      if (!bookId) return;

      await supabase.from('user_library').upsert({
        user_id:    user.id,
        book_id:    bookId,
        shelf:      'loved',
        date_added: new Date().toISOString(),
      });

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('username')
        .eq('user_id', user.id)
        .maybeSingle();

      await supabase.from('five_star_reads').insert({
        user_id:         user.id,
        username:        profile?.username || 'reader',
        title:           book.title,
        author_name:     book.author,
        cover_image_url: book.cover_image_url || null,
        genres:          allGenreNames,
        book_id:         bookId,
      });

      // Manually-entered tags. resolveAllTagsToBookTags handles matching
      // against existing tropes and queuing unrecognized ones for admin review.
      //
      // PR5 (docs/genre-subgenre-build-spec.md): genre is still passed in
      // here so resolveAllTagsToBookTags's genre-name resolution keeps
      // running (unrelated tropes/tone/spice/warnings share the same call),
      // but the resulting tag_type: 'genre' rows are filtered out before the
      // book_tags upsert loop below -- book_tags stopped being a genre store
      // in this PR. The actual write is the book_genres/book_subgenres
      // mirror a few lines down (writeReaderGenrePicks), which already runs
      // independently of this loop.
      const resolvedBookTags = await resolveAllTagsToBookTags(
        {
          tropes:   tags.tropes   || [],
          tone:     tags.moods    || [],
          genre:    allGenreNames,
          spice:    tags.spice    || null,
          warnings: tags.warnings || [],
        },
        'reader'
      );

      for (const bt of resolvedBookTags) {
        if (!bt.trope_id || bt.tag_type === 'genre') continue;
        await supabase.from('book_tags').upsert(
          { book_id: bookId, ...bt },
          { onConflict: 'book_id,trope_id', ignoreDuplicates: true }
        );
      }

      // book_genres/book_subgenres mirror (PR3 Finding 3) -- same ids
      // already selected above, no re-lookup by name needed. Secondary to
      // the save itself, so it never blocks it, but the reader is told
      // when it fails rather than being left thinking it landed.
      const { error: genreTablesErr } = await writeReaderGenrePicks(bookId, tags.genreIds || [], tags.subgenreIds || []);
      if (genreTablesErr) {
        console.error('CSV import: book_genres/book_subgenres write failed:', genreTablesErr);
        addToast(`"${book.title}" was added, but its genre/subgenre didn't save -- edit it to try again.`, 'error');
      }

      const allTagNames = [
        ...(tags.tropes   || []),
        ...(tags.moods    || []),
        ...allGenreNames,
        ...(tags.warnings || []),
        ...(tags.spice ? [tags.spice] : []),
      ];

      await supabase.from('book_submissions').insert({
        submitted_by:  user.id,
        title:         book.title,
        author_name:   book.author,
        cover_url:     book.cover_image_url || null,
        blurb:         book.blurb || null,
        isbn:          book.isbn13 || book.isbn || null,
        genres:        allGenreNames,
        selected_tags: allTagNames,
        status:        'pending',
      });

    } catch (err) {
      console.error('saveBook error:', err.message);
    }
  }

  const approvedCount = Object.values(decisions).filter(d => d === 'approved').length;
  const skippedCount  = Object.values(decisions).filter(d => d === 'skipped').length;
  const currentBook   = enrichedBooks[cardIndex];
  const currentTags   = editTags[cardIndex] || {};
  const currentGenreIds          = editGenreIds[cardIndex] || [];
  const currentSubgenreIds       = editSubgenreIds[cardIndex] || [];
  const currentSubgenreNamesById = editSubgenreNamesById[cardIndex] || {};
  const currentGenreNeeded       = genreNeeded[cardIndex] !== false;
  const combinedTagCount         = (currentTags.tropes || []).length + (currentTags.moods || []).length;
  // Genre only -- subgenre is asked for and never blocks, same as every
  // other reader surface (docs/genre-subgenre-build-spec.md, CSV section).
  const genreReady               = !currentGenreNeeded || currentGenreIds.length > 0;
  const canApproveCard           = combinedTagCount >= MIN_TAGS && genreReady;
  // GenreSubgenrePicker's setters are React setState-style (may receive a
  // function) -- this reroutes whichever call shape it uses into the
  // per-card map, same pattern editTags's own updaters use, generalized
  // for the array (genre/subgenre ids) and object (subgenreNamesById) cases.
  function updateCardGenreMap(setter, defaultVal, updater) {
    setter(p => ({ ...p, [cardIndex]: typeof updater === 'function' ? updater(p[cardIndex] ?? defaultVal) : updater }));
  }

  // ── Minimized pill ────────────────────────────────────────────────────────
  if (isMinimized && (step === 'processing' || step === 'enriching')) {
    const prog = step === 'processing' ? processProgress : enrichProgress;
    const pct  = prog.total > 0 ? Math.round((prog.current / prog.total) * 100) : 0;
    return (
      <div onClick={() => setIsMinimized(false)} style={{ position: 'fixed', bottom: 24, right: 24, background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', padding: '14px 20px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', zIndex: 1000, border: '1px solid var(--brand-sand)', minWidth: 300, cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          <span style={{ fontSize: 'var(--font-size-xxl)' }}>⏳</span>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 2px' }}>
              {step === 'processing' ? 'Importing library...' : 'Finding your books...'}
            </p>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>{prog.current} / {prog.total}</p>
          </div>
          <span style={{ fontSize: 'var(--font-size-xxl)', fontWeight: 700, color: 'var(--feedback-warning)' }}>{pct}%</span>
        </div>
        <div style={{ height: 4, background: 'var(--brand-sand)', borderRadius: 2, overflow: 'hidden', marginTop: 'var(--space-10)' }}>
          <div style={{ height: '100%', background: 'var(--feedback-warning)', width: `${pct}%`, transition: 'width 0.3s', borderRadius: 2 }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }}
      onClick={['upload', 'stats', 'select', 'done'].includes(step) ? onClose : undefined}>
      <div style={{ background: 'var(--brand-paper)', borderRadius: 24, maxWidth: 620, width: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.25)', scrollbarWidth: 'none' }}
        onClick={e => e.stopPropagation()}>

        {/* ── UPLOAD ── */}
        {step === 'upload' && (
          <div style={{ padding: 36 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 8px' }}>📚 Import Your Reading History</h2>
            <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-500)', margin: '0 0 24px', lineHeight: 1.6 }}>
              Import your Goodreads CSV to set your reading stats, track your library and share your 5-star obsessions with the community.
            </p>

            <div style={{ background: 'var(--feedback-warning-bg)', borderRadius: 14, padding: 'var(--space-lg)', marginBottom: 'var(--space-xl)', border: '1px solid var(--feedback-warning-border)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-md)', marginBottom: 'var(--space-sm)' }}>
                <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--feedback-warning-ink)', margin: 'var(--space-none)' }}>📋 How to export from Goodreads:</p>
                <a href="https://www.goodreads.com/review/import" target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 'var(--radius-md)', flexShrink: 0, background: 'var(--feedback-warning-ink)', color: 'var(--feedback-warning-bg)', fontSize: 'var(--font-size-xs)', fontWeight: 700, textDecoration: 'none' }}>
                  Open Goodreads ↗
                </a>
              </div>
              <ol style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-warning-ink)', margin: 'var(--space-none)', paddingLeft: 'var(--space-xl)' }}>
                <li>Click "Export Library" on the page that opens</li>
                <li>Download the CSV file</li>
                <li>Upload it here</li>
              </ol>
            </div>

            <label htmlFor="csv-file" style={{ display: 'block', padding: '36px 20px', border: '2px dashed var(--neutral-300)', borderRadius: 14, textAlign: 'center', cursor: 'pointer', background: file ? 'var(--feedback-success-bg)' : 'var(--brand-white)', transition: 'all 0.2s', marginBottom: 'var(--space-xl)' }}>
              <input id="csv-file" type="file" accept=".csv" onChange={handleFileSelect} style={{ display: 'none' }} />
              {file ? (
                <><div style={{ fontSize: 'var(--font-size-h1)', marginBottom: 'var(--space-sm)' }}>✅</div><p style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: '#166534', margin: '0 0 4px' }}>{file.name}</p><p style={{ fontSize: 'var(--font-size-sm)', color: '#15803d', margin: 'var(--space-none)' }}>Click to change</p></>
              ) : (
                <><div style={{ fontSize: 'var(--font-size-h1)', marginBottom: 'var(--space-sm)' }}>📁</div><p style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 4px' }}>Click or drag & drop your CSV</p><p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>Supports Goodreads, StoryGraph & LibraryThing</p></>
              )}
            </label>

            <div style={{ display: 'flex', gap: 'var(--space-10)' }}>
              <button onClick={onClose} style={{ flex: 1, padding: '13px', background: 'var(--brand-white)', color: 'var(--neutral-500)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-md)', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleUpload} disabled={!file}
                style={{ flex: 2, padding: '13px', background: file ? 'var(--neutral-800)' : 'var(--brand-sand)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: file ? 'pointer' : 'not-allowed' }}>
                📊 Upload & Import
              </button>
            </div>
          </div>
        )}

        {/* ── PROCESSING ── */}
        {step === 'processing' && (
          <div style={{ padding: 'var(--space-h4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-xxl)' }}>
              <div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px' }}>Importing your library...</h2>
                <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>Adding your read books and building your stats</p>
              </div>
              <button onClick={() => setIsMinimized(true)} style={{ padding: '8px 14px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-600)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 'var(--space-6)' }}>
                ↓ Minimize
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', marginBottom: 'var(--space-sm)' }}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 'var(--space-md)' }}>{processProgress.label}</span>
              <span style={{ flexShrink: 0 }}>{processProgress.current} / {processProgress.total}</span>
            </div>
            <div style={{ height: 8, background: 'var(--brand-sand)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', marginBottom: 'var(--space-lg)' }}>
              <div style={{ height: '100%', background: 'linear-gradient(90deg, var(--neutral-800), var(--neutral-600))', borderRadius: 'var(--radius-sm)', transition: 'width 0.3s', width: `${processProgress.total > 0 ? (processProgress.current / processProgress.total) * 100 : 0}%` }} />
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-10)', flexWrap: 'wrap' }}>
              {['📚 Adding to library', '📊 Building stats', '⭐ Finding 5-stars'].map((label, i) => (
                <span key={i} style={{ fontSize: 'var(--font-size-xs)', padding: '5px 12px', borderRadius: 'var(--radius-xxl)', background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', color: 'var(--neutral-500)' }}>{label}</span>
              ))}
            </div>
          </div>
        )}

        {/* ── STATS ── */}
        {step === 'stats' && stats && (
          <div style={{ padding: 36 }}>
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <div style={{ width: 72, height: 72, borderRadius: 'var(--radius-circle)', background: 'var(--brand-cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <span style={{ fontSize: 'var(--font-size-h2)' }}>📊</span>
              </div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 8px' }}>Reading Stats uploaded!</h2>
              <p style={{ fontSize: 15, color: 'var(--neutral-500)', margin: '0 0 4px' }}>
                We found <strong style={{ color: 'var(--neutral-800)' }}>{stats.totalBooks} books</strong> in your Goodreads export
              </p>
              <p style={{ fontSize: 15, color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>
                including <strong style={{ color: 'var(--feedback-warning)' }}>{stats.fiveStarCount} five-star reads</strong>
              </p>
            </div>

            <div style={{ background: 'var(--brand-white)', borderRadius: 14, padding: '16px 20px', border: '1px solid var(--brand-sand)', marginBottom: 'var(--space-lg)' }}>
              <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-400)', letterSpacing: 1, textTransform: 'uppercase', margin: '0 0 12px' }}>Rating Breakdown</p>
              {[5, 4, 3, 2, 1].map(star => {
                const count = stats.ratingCounts[star] || 0;
                const pct = stats.totalBooks > 0 ? (count / stats.totalBooks) * 100 : 0;
                return (
                  <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', minWidth: 18 }}>{star}★</span>
                    <div style={{ flex: 1, height: 7, background: 'var(--neutral-100)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: star === 5 ? 'var(--feedback-warning)' : star === 4 ? '#f59e0b' : 'var(--neutral-300)', width: `${pct}%`, borderRadius: 'var(--radius-sm)', transition: 'width 0.6s ease' }} />
                    </div>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', minWidth: 24, textAlign: 'right' }}>{count}</span>
                  </div>
                );
              })}
            </div>

            {stats.fiveStarCount > 0 && (
              <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', padding: 'var(--space-xxl)', border: '1px solid var(--brand-sand)', marginBottom: 'var(--space-xl)' }}>
                <div style={{ display: 'flex', gap: 'var(--space-14)', alignItems: 'flex-start' }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, var(--feedback-success), #10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 'var(--font-size-h4)' }}>🎲</span>
                  </div>
                  <div>
                    <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>Add your 5-star reads to the community?</h3>
                    <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 8px', lineHeight: 1.5 }}>
                      Share your 5-star obsessions with the community. Pick the ones you want to share, we'll only tag the books you choose.
                    </p>
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: 'var(--space-none)', lineHeight: 1.4 }}>
                      You'll select your favorites, then add each book's tags yourself before it's added: genres, tropes, moods, spice, all of it.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 'var(--space-10)' }}>
              <button onClick={() => { onComplete?.(); onClose(); }}
                style={{ flex: 1, padding: 'var(--space-14)', background: 'var(--brand-white)', color: 'var(--neutral-500)', border: '1px solid var(--brand-sand)', borderRadius: 14, fontSize: 'var(--font-size-md)', fontWeight: 500, cursor: 'pointer' }}>
                Skip for now
              </button>
              {stats.fiveStarCount > 0 && (
                <button onClick={() => setStep('select')}
                  style={{ flex: 2, padding: 'var(--space-14)', background: 'var(--neutral-800)', color: 'var(--brand-paper)', border: 'none', borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                  Yes, let's do it
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── SELECT ── */}
        {step === 'select' && (
          <div style={{ padding: 'var(--space-h3)' }}>
            <div style={{ textAlign: 'center', marginBottom: 'var(--space-xl)' }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>Pick your 5-star reads</h2>
              <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>Select the books you'd like to share. We'll only tag the ones you pick.</p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-10)' }}>
              <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)' }}>{selectedIndexes.size} of {fiveStarBooks.length} selected</span>
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                <button onClick={() => setSelectedIndexes(new Set(fiveStarBooks.map((_, i) => i)))} style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Select all</button>
                <span style={{ color: 'var(--neutral-300)' }}>·</span>
                <button onClick={() => setSelectedIndexes(new Set())} style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', background: 'none', border: 'none', cursor: 'pointer' }}>Deselect all</button>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', marginBottom: 'var(--space-xl)', maxHeight: 360, overflowY: 'auto', scrollbarWidth: 'none' }}>
              {fiveStarBooks.map((b, i) => {
                const checked = selectedIndexes.has(i);
                return (
                  <div key={i} onClick={() => {
                    const next = new Set(selectedIndexes);
                    checked ? next.delete(i) : next.add(i);
                    setSelectedIndexes(next);
                  }} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', background: checked ? '#fefce8' : 'var(--brand-white)', borderRadius: 'var(--radius-lg)', padding: '10px 14px', border: checked ? '1.5px solid var(--feedback-warning)' : '1px solid var(--brand-sand)', cursor: 'pointer', transition: 'all 0.15s' }}>
                    <div style={{ width: 20, height: 20, borderRadius: 5, border: checked ? '2px solid var(--feedback-warning)' : '2px solid var(--neutral-300)', background: checked ? 'var(--feedback-warning)' : 'var(--brand-white)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {checked && <span style={{ color: 'var(--brand-white)', fontSize: 'var(--font-size-xs)', fontWeight: 800 }}>✓</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: checked ? 'var(--neutral-800)' : 'var(--neutral-400)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.title}</p>
                      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>by {b.author}</p>
                    </div>
                    <span style={{ fontSize: 'var(--font-size-sm)', color: '#fbbf24', flexShrink: 0 }}>★★★★★</span>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-10)' }}>
              <button onClick={() => setStep('stats')} style={{ flex: 1, padding: '13px', background: 'var(--brand-white)', color: 'var(--neutral-500)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-md)', fontWeight: 600, cursor: 'pointer' }}>← Back</button>
              <button onClick={handleStartEnriching} disabled={selectedIndexes.size === 0}
                style={{ flex: 2, padding: '13px', background: selectedIndexes.size > 0 ? 'var(--neutral-800)' : 'var(--brand-sand)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: selectedIndexes.size > 0 ? 'pointer' : 'not-allowed' }}>
                Tag {selectedIndexes.size} book{selectedIndexes.size !== 1 ? 's' : ''} →
              </button>
            </div>
          </div>
        )}

        {/* ── ENRICHING ── */}
        {step === 'enriching' && (
          <div style={{ padding: 'var(--space-h4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-xxl)' }}>
              <div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px' }}>Finding your books...</h2>
                <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>Pulling covers and blurbs for {enrichProgress.total} book{enrichProgress.total !== 1 ? 's' : ''}</p>
              </div>
              <button onClick={() => setIsMinimized(true)} style={{ padding: '8px 14px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-600)', cursor: 'pointer' }}>↓ Minimize</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', marginBottom: 'var(--space-sm)' }}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 'var(--space-md)' }}>{enrichProgress.label}</span>
              <span style={{ flexShrink: 0 }}>{enrichProgress.current}/{enrichProgress.total}</span>
            </div>
            <div style={{ height: 8, background: 'var(--brand-sand)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', marginBottom: 'var(--space-lg)' }}>
              <div style={{ height: '100%', background: 'linear-gradient(90deg, var(--feedback-warning), #f59e0b)', borderRadius: 'var(--radius-sm)', transition: 'width 0.4s', width: `${enrichProgress.total > 0 ? (enrichProgress.current / enrichProgress.total) * 100 : 0}%` }} />
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-10)', flexWrap: 'wrap' }}>
              {['📚 Goodreads data', '📖 Open Library', '📋 Platform tags'].map((label, i) => (
                <span key={i} style={{ fontSize: 'var(--font-size-xs)', padding: '5px 12px', borderRadius: 'var(--radius-xxl)', background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', color: 'var(--neutral-500)' }}>{label}</span>
              ))}
            </div>
          </div>
        )}

        {/* ── REVIEW ── */}
        {step === 'review' && currentBook && (
          <div style={{ padding: 'var(--space-xxl)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', marginBottom: 'var(--space-md)' }}>
              <div style={{ flex: 1, height: 4, background: 'var(--brand-sand)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: 'var(--neutral-800)', width: `${(cardIndex / enrichedBooks.length) * 100}%`, transition: 'width 0.3s' }} />
              </div>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', fontWeight: 600, whiteSpace: 'nowrap' }}>{cardIndex + 1} / {enrichedBooks.length}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-lg)', marginBottom: 'var(--space-14)' }}>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-success)', fontWeight: 600 }}>✓ {approvedCount} added</span>
              <span style={{ color: 'var(--neutral-300)' }}>·</span>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)' }}>{skippedCount} skipped</span>
            </div>

            {saving ? (
              <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', border: '1px solid var(--brand-sand)', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <div style={{ background: 'linear-gradient(135deg, var(--neutral-800), var(--neutral-700))', padding: '20px 24px' }}>
                  <div style={{ display: 'flex', gap: 'var(--space-14)', alignItems: 'center' }}>
                    <div style={{ width: 52, height: 72, borderRadius: '3px 7px 7px 3px', overflow: 'hidden', background: 'var(--neutral-600)', flexShrink: 0 }}>
                      {currentBook.cover_image_url
                        ? <img src={currentBook.cover_image_url} alt={currentBook.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>📚</div>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--brand-white)', margin: '0 0 3px' }}>{currentBook.title}</h3>
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'rgba(255,255,255,0.6)', margin: '0 0 8px' }}>by {currentBook.author}</p>
                      <span style={{ fontSize: 'var(--font-size-base)', color: '#fbbf24' }}>★★★★★</span>
                    </div>
                  </div>
                </div>
                <div style={{ padding: '40px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-lg)' }}>
                  <div style={{ width: 48, height: 48, borderRadius: 'var(--radius-circle)', background: 'linear-gradient(135deg, var(--feedback-success), #10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(16,185,129,0.25)' }}>
                    <div style={{ width: 22, height: 22, border: '3px solid rgba(255,255,255,0.4)', borderTopColor: 'var(--brand-white)', borderRadius: 'var(--radius-circle)', animation: 'spin 0.8s linear infinite' }} />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px' }}>Adding to your library...</p>
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>Saving tags and submitting for review</p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', width: '100%', maxWidth: 280 }}>
                    {[
                      { icon: '📚', label: 'Adding to your library' },
                      { icon: '🏷️', label: 'Saving tags & tropes' },
                      { icon: '📬', label: 'Submitting for review' },
                    ].map((s, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '8px 14px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', border: '1px solid var(--brand-sand)' }}>
                        <span style={{ fontSize: 'var(--font-size-md)' }}>{s.icon}</span>
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)' }}>{s.label}</span>
                        <div style={{ marginLeft: 'auto', width: 12, height: 12, border: '2px solid var(--brand-sand)', borderTopColor: 'var(--feedback-success)', borderRadius: 'var(--radius-circle)', animation: `spin ${0.8 + i * 0.2}s linear infinite` }} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', overflow: 'hidden', border: '1px solid var(--brand-sand)', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', transform: swipeDir === 'right' ? 'translateX(120%) rotate(8deg)' : swipeDir === 'left' ? 'translateX(-120%) rotate(-8deg)' : 'none', opacity: swipeDir ? 0 : 1, transition: swipeDir ? 'all 0.3s ease-in' : 'none' }}>
                <div style={{ background: 'linear-gradient(135deg, var(--neutral-800), var(--neutral-700))', padding: '20px 24px' }}>
                  <div style={{ display: 'flex', gap: 'var(--space-14)', alignItems: 'center' }}>
                    <div style={{ width: 52, height: 72, borderRadius: '3px 7px 7px 3px', overflow: 'hidden', background: 'var(--neutral-600)', flexShrink: 0 }}>
                      {currentBook.cover_image_url
                        ? <img src={currentBook.cover_image_url} alt={currentBook.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>📚</div>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--brand-white)', margin: '0 0 3px' }}>{currentBook.title}</h3>
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'rgba(255,255,255,0.6)', margin: '0 0 8px' }}>by {currentBook.author}</p>
                      <span style={{ fontSize: 'var(--font-size-base)', color: '#fbbf24' }}>★★★★★</span>
                    </div>
                  </div>
                </div>

                <div style={{ padding: '16px 24px 8px', maxHeight: 380, overflowY: 'auto', scrollbarWidth: 'none' }}>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: '0 0 12px', lineHeight: 1.4 }}>Add this book's tags below before approving.</p>

                  {currentGenreNeeded && (
                  <div style={{ marginBottom: 'var(--space-10)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-6)' }}>
                      <span style={{ fontSize: 'var(--font-size-sm)' }}>📚</span>
                      <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: 0.5 }}>GENRE</span>
                      <span style={{ fontSize: 'var(--font-size-micro)', color: 'var(--feedback-error)' }}>*</span>
                    </div>
                    <GenreSubgenrePicker
                      Chip={CsvGenreChip}
                      genres={csvGenres}
                      canonSubgenresForGenre={csvCanonSubgenresForGenre}
                      matchIndex={csvMatchIndex}
                      upsertPendingSubgenre={csvUpsertPendingSubgenre}
                      selectedGenreIds={currentGenreIds}
                      setSelectedGenreIds={u => updateCardGenreMap(setEditGenreIds, [], u)}
                      selectedSubgenreIds={currentSubgenreIds}
                      setSelectedSubgenreIds={u => updateCardGenreMap(setEditSubgenreIds, [], u)}
                      subgenreNamesById={currentSubgenreNamesById}
                      setSubgenreNamesById={u => updateCardGenreMap(setEditSubgenreNamesById, {}, u)}
                      loading={csvGenreLoading}
                      error={csvGenreError}
                    />
                  </div>
                  )}
                  <TagRow icon="🏷️" label="TROPES (3 combined with mood, minimum)" tags={currentTags.tropes} colorKey="tropes" required
                    onRemove={i => removeTag(cardIndex, 'tropes', i)} onAdd={v => addTag(cardIndex, 'tropes', v)} />
                  <TagRow icon="🎭" label="MOOD & TONE" tags={currentTags.moods} colorKey="moods"
                    onRemove={i => removeTag(cardIndex, 'moods', i)} onAdd={v => addTag(cardIndex, 'moods', v)} />
                  <TagRow icon="⚠️" label="CONTENT WARNINGS" tags={currentTags.warnings} colorKey="warnings"
                    onRemove={i => removeTag(cardIndex, 'warnings', i)} onAdd={v => addTag(cardIndex, 'warnings', v)} />

                  <div style={{ marginBottom: 'var(--space-10)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-sm)' }}>
                      <span style={{ fontSize: 'var(--font-size-sm)' }}>🌶️</span>
                      <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: 0.5 }}>SPICE LEVEL</span>
                      <span style={{ fontSize: 'var(--font-size-micro)', color: 'var(--feedback-error)' }}>*</span>
                    </div>
                    <div style={{ display: 'flex', gap: 5 }}>
                      {SPICE_LABELS.map((label, i) => {
                        const active = currentTags.spice === label;
                        return (
                          <button key={i} onClick={() => setSpice(cardIndex, label)}
                            style={{ flex: 1, padding: '8px 4px', borderRadius: 'var(--radius-10)', textAlign: 'center', cursor: 'pointer', background: active ? 'var(--neutral-800)' : 'var(--brand-white)', border: active ? '2px solid var(--neutral-800)' : '1px solid var(--brand-sand)', transition: 'all 0.15s' }}>
                            <div style={{ fontSize: 'var(--font-size-xxs)', marginBottom: 'var(--space-xxs)' }}>{i === 0 ? '🌸' : '🌶️'.repeat(i)}</div>
                            <p style={{ fontSize: 'var(--font-size-micro)', fontWeight: 700, color: active ? 'var(--brand-paper)' : 'var(--neutral-800)', margin: 'var(--space-none)' }}>{label}</p>
                            <p style={{ fontSize: 7, color: active ? 'var(--neutral-400)' : 'var(--neutral-500)', margin: 'var(--space-none)' }}>{SPICE_DESCS[i]}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {currentBook.blurb && (
                    <div style={{ background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', padding: 'var(--space-md)', border: '1px solid var(--brand-sand)', marginBottom: 'var(--space-10)' }}>
                      <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', letterSpacing: 1, margin: '0 0 6px', textTransform: 'uppercase' }}>About</p>
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', lineHeight: 1.6, margin: 'var(--space-none)', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{currentBook.blurb}</p>
                    </div>
                  )}
                </div>

                <div style={{ padding: '12px 24px 20px', borderTop: '1px solid var(--neutral-100)' }}>
                  <div style={{ display: 'flex', gap: 'var(--space-10)' }}>
                    <button onClick={() => handleDecision('skipped')}
                      style={{ flex: 1, padding: 'var(--space-14)', background: 'var(--brand-white)', color: 'var(--neutral-500)', border: '1px solid var(--brand-sand)', borderRadius: 14, fontSize: 'var(--font-size-md)', fontWeight: 600, cursor: 'pointer' }}>
                      ← Skip
                    </button>
                    <button onClick={() => handleDecision('approved')} disabled={!canApproveCard}
                      style={{ flex: 2, padding: 'var(--space-14)', background: canApproveCard ? 'linear-gradient(135deg, var(--feedback-success), #10b981)' : 'var(--brand-sand)', color: canApproveCard ? 'var(--brand-white)' : 'var(--neutral-500)', border: 'none', borderRadius: 14, fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: canApproveCard ? 'pointer' : 'not-allowed', boxShadow: canApproveCard ? '0 4px 12px rgba(16,185,129,0.25)' : 'none' }}>
                      {canApproveCard
                        ? 'Add as 5-Star Read ⭐'
                        : combinedTagCount < MIN_TAGS
                          ? `Add ${MIN_TAGS - combinedTagCount} more trope(s)/mood(s)`
                          : 'Pick a genre ↑'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── DONE ── */}
        {step === 'done' && (
          <div style={{ padding: 'var(--space-h4)', textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: 'var(--radius-circle)', background: 'linear-gradient(135deg, var(--feedback-success), #10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 8px 24px rgba(16,185,129,0.2)' }}>
              <span style={{ fontSize: 'var(--font-size-h2)' }}>🎉</span>
            </div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-h4)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 8px' }}>You're all done!</h2>
            <p style={{ fontSize: 15, color: 'var(--neutral-500)', margin: '0 0 4px' }}>
              <strong style={{ color: 'var(--neutral-800)' }}>{approvedCount} book{approvedCount !== 1 ? 's' : ''}</strong> added to your library and submitted for review.
            </p>
            {skippedCount > 0 && <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', margin: '0 0 24px' }}>{skippedCount} skipped</p>}

            <div style={{ background: 'var(--brand-white)', borderRadius: 14, padding: '16px 20px', border: '1px solid var(--brand-sand)', marginBottom: 'var(--space-xxl)', textAlign: 'left' }}>
              <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 4px' }}>What happens next?</p>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: 'var(--space-none)', lineHeight: 1.5 }}>
                Your books are in your library. They'll appear on the Discover page once reviewed and approved by our team.
              </p>
            </div>

           <button onClick={() => { onComplete?.(); onClose(); }}
  style={{ width: '100%', padding: 'var(--space-14)', background: 'var(--neutral-800)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
  Close
</button>
          </div>
        )}
      </div>
    </div>
  );
}